import { TOKEN_SESSION_NAME } from '@core/auth.service';
import { useStorage } from '@plugins/storage';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { HttpErrorResponse } from 'data/models/error.interface';
import { FilterParams } from 'data/models/filter.interface';

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      window.location.reload();
    }
    const httpErrorResponse: HttpErrorResponse = {
      ...error?.response?.data,
      httpStatus: error?.response?.status,
    };
    return { error: httpErrorResponse };
  },
);

export const useNinaApi = () => {
  const send = async (
    url: string,
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse> => {
    const storage = useStorage();
    const token = storage.get(TOKEN_SESSION_NAME);

    return axios(url, {
      responseType: 'json',
      baseURL: process.env.VITE_APP_NINA_API_URL,
      headers: {
        Authorization: `Bearer ${token as string}`,
        Accept: 'application/vnd.api+json',
        'Content-Type': 'application/vnd.api+json',
        'Access-Control-Allow-Origin': '*',
      },
      ...config,
    });
  };

  const get = <T extends object>(
    url: string,
    qs?: FilterParams & { responseType?: 'json' | 'blob' },
  ): Promise<{ data?: T; error?: HttpErrorResponse }> => {
    const { filter, orderBy, orderDirection, responseType, ...params } =
      qs || {};

    const apiParams: {
      'page[size]': number | undefined;
      'page[number]': number | undefined;
      include?: string;
      sort?: string;
      [key: string]: string | number | string[] | undefined;
    } = {
      ['include']: params.include?.join(',') || '',
      ['page[size]']: params.itemsPerPage,
      ['page[number]']: params.page,
    };

    if (params.include) {
      apiParams.include = params.include.join(',');
    }

    if (params.extraValues) {
      for (const [key, val] of Object.entries(params.extraValues)) {
        apiParams[key] = val;
      }
    }

    if (orderBy) {
      apiParams.sort = `${(orderDirection === 'desc' && '-') || ''}${orderBy}`;
    }

    return send(url, {
      params: Object.keys(filter || {}).reduce((acc, cur) => {
        if (!filter || !cur) {
          return acc;
        }

        const value = filter[cur];

        if (!value) {
          return acc;
        }

        acc[`filter[${cur}]`] = filter[cur];
        return acc;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }, apiParams as any),
      method: 'GET',
      responseType: responseType || 'json',
    });
  };

  const patch = <T extends object, U extends object>(
    url: string,
    body: Partial<T>,
  ): Promise<{ data?: U; error?: HttpErrorResponse }> => {
    return send(url, {
      data: body,
      method: 'PATCH',
    });
  };

  const put = <T extends object, U extends object>(
    url: string,
    body: Partial<T>,
  ): Promise<{ data?: U; error?: HttpErrorResponse }> => {
    return send(url, {
      data: body,
      method: 'PUT',
    });
  };

  const post = <T extends object, U extends object>(
    url: string,
    body: Partial<T>,
  ): Promise<{ data?: U; error?: HttpErrorResponse }> => {
    return send(url, {
      data: body,
      method: 'POST',
    });
  };

  const destroy = <T extends object>(
    url: string,
  ): Promise<{ data?: T; error?: HttpErrorResponse }> => {
    return send(url, {
      method: 'DELETE',
    });
  };

  return {
    get,
    send,
    post,
    patch,
    put,
    destroy,
  };
};
